import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import Alert from "../../../components/alert";
import Badge from "../../../components/badge";
import Button from "../../../components/button";
import ConfirmDialog, {
  ConfirmDialogType,
} from "../../../components/confirm-dialog";
import useScreenName from "../../../hooks/useScreenName";
import dispatch from "../../../middleware";
import { getApplicationById } from "../../../actions/application.action";
import {
  getAuditLog,
  manualOverride,
} from "../../../actions/credit-decision.action";
import { RoutePaths } from "../../../models";
import {
  CreditDecisionAuditLog,
  ManualOverrideRequest,
  RafikiUserFeatures,
} from "../../../models/creditDecision";
import { TermLoanApplicationDetails } from "../../../dto";
import { dateToDDMMYYYY, splitKeyWithSpace } from "../../../utils";

interface UserFeatureRow {
  label: string;
  required: string;
}

/**
 * Optional map of rafiki feature key -> display label override.
 * When provided, the mapped label is shown instead of the default `UserFeatureRow.label`.
 * Keyed by the `required` field (e.g. "EMPLOYEE_MONTHLY_GROSS_SALARY").
 */
export type FeatureLabelMap = Partial<Record<string, string>>;

const USER_FEATURE_ROWS: UserFeatureRow[] = [
  {
    label: "User Gross Salary",
    required: "EMPLOYEE_MONTHLY_GROSS_SALARY",
  },
  {
    label: "User Net Salary",
    required: "EMPLOYEE_MONTHLY_SALARY",
  },
  { label: "User Age", required: "USER_AGE_IN_YEARS" },
  {
    label: "Months Left for Retirement",
    required: "NUMBER_OF_DAYS_LEFT_FOR_RETIREMENT",
  },
  {
    label: "Latest Salary Credit Status",
    required: "IS_LATEST_SALARY_CREDIT_WITHIN_EXPECTED_DATE",
  },
  { label: "Credit Score", required: "CREDIT_SCORE" },
  {
    label: "Existing FOIR",
    required: "CURRENT_FOIR_OBLIGATION_AMOUNT_V4",
  },
  {
    label: "Active STU/EWA with Refyne",
    required: "ACTIVE_SALARY_TOPUP_COUNT",
  },
  {
    label: "Internal DPD",
    required: "LATEST_ACTIVE_SALARY_TOPUP_DPD",
  },
  {
    label: "Dpd30_Last_36M_0_3M_Outstanding_Amount",
    required: "DPD30_LAST_36M_0_3M_OUTSTANDING_AMOUNT",
  },
  {
    label: "Dpd90_Last_36M_0_6M_Outstanding_Amount",
    required: "DPD90_LAST_36M_0_6M_OUTSTANDING_AMOUNT",
  },
];

type DialogMode = "reject" | "approve" | "approveWithTerms" | null;
export default function ManualReviewDetailPage({
  featureLabelMap,
}: {
  featureLabelMap?: FeatureLabelMap;
} = {}) {
  useScreenName("Manual Review Detail");
  const { appId } = useParams() as { appId: string };
  const history = useHistory();
  const storeDispatch = useDispatch();

  const [application, setApplication] =
    useState<TermLoanApplicationDetails | null>(null);
  const [auditLog, setAuditLog] = useState<CreditDecisionAuditLog | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingAudit, setLoadingAudit] = useState(false);
  const [dialogMode, setDialogMode] = useState<DialogMode>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [alertType, setAlertType] = useState<"success" | "danger">("success");
  const [showRawSnapshot, setShowRawSnapshot] = useState(false);

  useEffect(() => {
    setLoading(true);
    setAlertMessage(null);
    dispatch(storeDispatch, getApplicationById(appId))
      .then((data: TermLoanApplicationDetails) => {
        setApplication(data);
      })
      .catch((err: Error) => {
        setAlertType("danger");
        setAlertMessage(err?.message || "Failed to fetch application");
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appId]);

  const creditDecisionId = application?.creditDecisionId;

  useEffect(() => {
    if (!creditDecisionId) return;

    setLoadingAudit(true);
    dispatch(storeDispatch, getAuditLog(creditDecisionId))
      .then((data: CreditDecisionAuditLog) => setAuditLog(data))
      .catch(() => {
        // Audit log is optional; silently ignore
      })
      .finally(() => setLoadingAudit(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creditDecisionId]);

  const features: RafikiUserFeatures | undefined = auditLog?.responseSnapshot
    ?.salaryTopup?.userFeatures as RafikiUserFeatures | undefined;

  function handleBack() {
    history.push(RoutePaths.CREDIT_DECISION_MANUAL_REVIEW);
  }

  function renderFeatureCell(row: UserFeatureRow): string {
    const value = features?.[row.required];
    return value === null || value === undefined ? "-" : String(value);
  }

  async function handleDialogSubmit(
    type: ConfirmDialogType,
    fieldData: { [x: string]: string }
  ) {
    if (!dialogMode) return;
    setIsSubmitting(true);
    try {
      let payload: ManualOverrideRequest;
      if (dialogMode === "reject") {
        payload = {
          applicationId: appId,
          status: "REJECTED",
          reasons: [fieldData.reason],
        };
      } else if (dialogMode === "approve") {
        payload = {
          applicationId: appId,
          status: "IN_PROGRESS",
          reasons: [fieldData.reason],
        };
      } else {
        payload = {
          applicationId: appId,
          status: "IN_PROGRESS",
          reasons: [fieldData.reason],
          updatedTerms: {
            amount: Number(fieldData.amount),
            tenureInMonths: Number(fieldData.tenureInMonths),
            irpa: fieldData.irpa !== "" ? Number(fieldData.irpa) : undefined,
            pf: fieldData.pf !== "" ? Number(fieldData.pf) : undefined,
          },
        };
      }
      await dispatch(storeDispatch, manualOverride(appId, payload));
      setDialogMode(null);
      setAlertType("success");
      setAlertMessage("Application updated successfully");
      setTimeout(() => {
        history.push(RoutePaths.CREDIT_DECISION_MANUAL_REVIEW);
      }, 1500);
      return { message: "Application updated successfully" };
    } catch (err) {
      const error = err as Error;
      setAlertType("danger");
      setAlertMessage(error?.message || "Failed to update application");
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2>Manual Review Detail</h2>
          <Button variant="blue-outline" onClick={handleBack}>
            ← Back to List
          </Button>
        </div>
        <div className="dashboard-container">
          <p className="text-center text-muted">Loading...</p>
        </div>
      </div>
    );
  }

  if (!application) {
    return (
      <div>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2>Manual Review Detail</h2>
          <Button variant="blue-outline" onClick={handleBack}>
            ← Back to List
          </Button>
        </div>
        <div className="dashboard-container">
          <p className="text-center text-danger">Application not found.</p>
        </div>
      </div>
    );
  }

  const statusData =
    typeof application.status === "string"
      ? application.status
      : application.status.data;

  const isPendingManualReview =
    String(statusData) === "PENDING_MANUAL_REVIEW";

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Manual Review Detail</h2>
        <Button variant="blue-outline" onClick={handleBack}>
          ← Back to List
        </Button>
      </div>


      {alertMessage && (
        <Alert
          type={alertType}
          message={alertMessage}
          canDismiss
          onDismissClick={() => setAlertMessage(null)}
        />
      )}

      {/* Application Data Section */}
      <div className="dashboard-container mb-4">
        <h5 className="mb-3">Application Data</h5>
        <table className="table table-sm table-bordered">
          <tbody>
            <tr>
              <td className="fw-bold" style={{ width: "30%" }}>
                Application ID
              </td>
              <td style={{ wordBreak: "break-all" }}>{application._id}</td>
            </tr>
            <tr>
              <td className="fw-bold">User ID</td>
              <td>{application.user?._id || application.userId || "-"}</td>
            </tr>
            <tr>
              <td className="fw-bold">User Name</td>
              <td>{application.user?.employeeName || "-"}</td>
            </tr>
            <tr>
              <td className="fw-bold">Status</td>
              <td>
                <Badge
                  type={
                    statusData === "APPROVED"
                      ? "success"
                      : statusData === "REJECTED"
                      ? "reject"
                      : "pending"
                  }
                >
                  {splitKeyWithSpace(statusData)}
                </Badge>
              </td>
            </tr>
            <tr>
              <td className="fw-bold">Approved Amount</td>
              <td>{application.approvedAmount || "-"}</td>
            </tr>
            <tr>
              <td className="fw-bold">Tenure (Months)</td>
              <td>{application.tenureInMonths || "-"}</td>
            </tr>
            <tr>
              <td className="fw-bold">IRPA</td>
              <td>{application.irpa || "-"}</td>
            </tr>
            <tr>
              <td className="fw-bold">PF</td>
              <td>{application.pf || "-"}</td>
            </tr>
            <tr>
              <td className="fw-bold">Created At</td>
              <td>
                {application.createdAt
                  ? dateToDDMMYYYY(application.createdAt)
                  : "-"}
              </td>
            </tr>
            <tr>
              <td className="fw-bold">Credit Decision ID</td>
              <td>{application.creditDecisionId || "-"}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Credit Decision Section */}
      {creditDecisionId && (
        <div className="dashboard-container mb-4">
          <h5 className="mb-3">Credit Decision</h5>
          {loadingAudit ? (
            <p className="text-muted">Loading credit decision...</p>
          ) : auditLog ? (
            <>
              <table className="table table-sm table-bordered mb-3">
                <tbody>
                  <tr>
                    <td className="fw-bold" style={{ width: "30%" }}>
                      Approved
                    </td>
                    <td>
                      <Badge type={auditLog.approved ? "success" : "reject"}>
                        {auditLog.approved ? "Yes" : "No"}
                      </Badge>
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Evaluated At</td>
                    <td>
                      {auditLog.evaluatedAt
                        ? dateToDDMMYYYY(auditLog.evaluatedAt)
                        : "-"}
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Purpose</td>
                    <td>{splitKeyWithSpace(auditLog.purpose)}</td>
                  </tr>
                  <tr>
                    <td className="fw-bold">Product Type</td>
                    <td>{splitKeyWithSpace(auditLog.productType)}</td>
                  </tr>
                </tbody>
              </table>

              {/* User Features Sub-section */}
              <h6 className="mb-2 mt-3">Credit Decision Features</h6>
              <table className="table table-sm table-bordered">
                <thead>
                  <tr>
                    <th style={{ width: "5%" }}>Sl No</th>
                    <th style={{ width: "25%" }}>Field</th>
                    <th>Value</th>
                  </tr>
                </thead>
                <tbody>
                  {USER_FEATURE_ROWS.map((row, index) => (
                    <tr key={row.required}>
                      <td>{index + 1}</td>
                      <td>{featureLabelMap?.[row.required] ?? row.label}</td>
                      <td>{renderFeatureCell(row)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Raw Snapshot Toggle */}
              <div className="mt-3">
                <Button
                  variant="text"
                  onClick={() => setShowRawSnapshot((prev) => !prev)}
                >
                  {showRawSnapshot ? "Hide" : "Show"} Raw Response Snapshot
                </Button>
                {showRawSnapshot && (
                  <pre
                    className="bg-light p-3 rounded mt-2"
                    style={{
                      maxHeight: "500px",
                      overflow: "auto",
                      fontSize: "12px",
                    }}
                  >
                    {JSON.stringify(auditLog.responseSnapshot, null, 2)}
                  </pre>
                )}
              </div>
            </>
          ) : (
            <p className="text-muted">
              Credit decision audit log not available.
            </p>
          )}
        </div>
      )}

      {/* Action Step */}
      <div className="dashboard-container mb-4">
        <h5 className="mb-3">Action</h5>
        <div className="d-flex flex-wrap">
          <div className="mr-3 mb-2">
            <Button
              variant="danger-outline"
              onClick={() => setDialogMode("reject")}
              disabled={isSubmitting || !isPendingManualReview}
            >
              Reject
            </Button>
          </div>
          <div className="mr-3 mb-2">
            <Button
              variant="success-outline"
              onClick={() => setDialogMode("approve")}
              disabled={isSubmitting || !isPendingManualReview}
            >
              Approve
            </Button>
          </div>
          <div className="mb-2">
            <Button
              variant="success"
              onClick={() => setDialogMode("approveWithTerms")}
              disabled={isSubmitting || !isPendingManualReview}
            >
              Approve with Terms Change
            </Button>
          </div>
        </div>
      </div>

      {/* Reject Dialog */}
      <ConfirmDialog
        active={dialogMode === "reject"}
        type="reject"
        title="Reject Application"
        message="Are you sure you want to reject this application?"
        onSubmit={handleDialogSubmit}
        onCancel={() => setDialogMode(null)}
        submitButtonText="Reject"
        inputFields={[
          {
            name: "reason",
            label: "Reason",
            type: "text",
            required: true,
            placeholder: "Enter reason for rejection",
          },
        ]}
      />

      {/* Approve Dialog */}
      <ConfirmDialog
        active={dialogMode === "approve"}
        type="success"
        title="Approve Application"
        message="Are you sure you want to approve this application?"
        onSubmit={handleDialogSubmit}
        onCancel={() => setDialogMode(null)}
        submitButtonText="Approve"
        inputFields={[
          {
            name: "reason",
            label: "Remarks",
            type: "text",
            required: true,
            placeholder: "Enter remarks",
          },
        ]}
      />

      {/* Approve with Terms Dialog */}
      <ConfirmDialog
        active={dialogMode === "approveWithTerms"}
        type="success"
        title="Approve with Terms Change"
        message="Enter the new terms for this application."
        onSubmit={handleDialogSubmit}
        onCancel={() => setDialogMode(null)}
        submitButtonText="Submit"
        inputFields={[
          {
            name: "amount",
            label: "Amount",
            type: "number",
            inputMode: "numeric",
            required: true,
            placeholder: "Enter amount",
          },
          {
            name: "tenureInMonths",
            label: "Tenure (months)",
            type: "number",
            inputMode: "numeric",
            required: true,
            placeholder: "Enter tenure",
          },
          {
            name: "irpa",
            label: "IRPA",
            type: "number",
            inputMode: "numeric",
            required: false,
            placeholder: "Enter IRPA",
          },
          {
            name: "pf",
            label: "Processing Fee",
            type: "number",
            inputMode: "numeric",
            required: false,
            placeholder: "Enter processing fee",
          },
          {
            name: "reason",
            label: "Remarks",
            type: "text",
            required: true,
            placeholder: "Enter remarks",
          },
        ]}
      />
    </div>
  );
}
